import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  RefObject,
} from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
// import cn from "classnames";
import { Alert, AlertType } from "@components/new/Alert";
import Checkbox from "@components/new/Checkbox";
import { Tooltip } from "@components/new/Tooltip";
import { Marketplace } from "@components/new/Marketplace";
import {
  IconButton,
  IconButtonSize,
  IconButtonType,
} from "@components/new/IconButton";
import SearchInput from "@components/new/SearchInput";
import Photo from "@components/new/Photo";
import TextInput from "@components/TextInput/TextInput";
import { Title2, Title3 } from "@components/new/Typo";
import ConfirmModal from "@components/modals/ConfirmModal";
import { ButtonType } from "@components/new/Button";
import { Table, TableCellLink, TableColumn } from "@components/new/Table";
// import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Accordeon } from "@components/new/Accordeon";
import {
  createFBPMatchingRequest,
  updateMatching,
  searchItems,
  isWbRequestItem,
  isYmRequestItem,
  isAliRequestItem,
  TYmItemSearch,
  TWbItemSearch,
  TAliItemSearch,
  TFBPMatchingItem,
  TWbMatchingItem,
  TYmMatchingItem,
  TAliMatchingItem,
} from "@api/apiClient/fbp";
import {
  SearchMenuComponentMP,
  SearchMenuComponentMPShort,
} from "./SearchMenuComponent";
import { FormHandle } from "./types";
import DelIcon from "@assets/icons/outline/trash-2.svg?react";
import DelIcon2 from "@assets/icons/solid/trash-fill.svg?react";
import InfoIcon from "@assets/icons/solid/circle-info.svg?react";
import StoreIcon from "@assets/icons/outline/store.svg?react";
// import emptyImg from '@assets/img/empty.png';
import styles from "./Form.module.scss";

type TItemSearch = TYmItemSearch | TWbItemSearch | TAliItemSearch;
type TMatchingItem = TWbMatchingItem | TYmMatchingItem | TAliMatchingItem;

export interface FormType {
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
}

const getSku = (data: TMatchingItem) => {
  if (isYmRequestItem(data)) return data.sku || "";
  if (isWbRequestItem(data)) return data.wbNmId || "";
  if (isAliRequestItem(data)) return data.skuCode || "";
  return ""
}

const Form = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  sellerWarehouseId,
  hasAliStores,
  hasWbStores,
  hasYmStores,
  matchingData,
  setAccordeonPage,
  accordeonPage,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean;
    isLoading?: boolean;
    alert?: React.ReactNode;
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  sellerWarehouseId: string;
  hasWbStores: boolean;
  hasYmStores: boolean;
  hasAliStores: boolean;
  matchingData?: TFBPMatchingItem;
  setAccordeonPage: (v: string) => void;
  accordeonPage: string;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const [name, setName] = useState<string>("");
  const [isNamePristine, setNamePristine] = useState<boolean>(true);
  const [items, setItems] = useState<TMatchingItem[]>([]);
  const [isWbSelected, setWbSelected] = useState(false);
  const [isYmSelected, setYmSelected] = useState(false);
  const [isAliSelected, setAliSelected] = useState(false);
  const [currentDelId, setCurrentDelId] = useState<string>("");
  const [isOpenDelItemWindow, setIsOpenDelItemWindow] =
    useState<boolean>(false);
  const [isOpenConfirmSaveWindow, setOpenConfirmSaveWindow] =
    useState<boolean>(false);
  const [isOpenDelMarketplaceWindow, setIsOpenDelMarketplaceWindow] =
    useState<boolean>(false);
  //const [isOpenAddMarketplaceWindow, setIsOpenAddMarketplaceWindow] =
  //  useState<boolean>(false);
  const [marketplaceToSwitch, setMarketplaceToSwitch] = useState<string>("");

  const handleMarketplaceSwitch = (marketplace: string, selected: boolean) => {
    if (!selected) {
      setMarketplaceToSwitch(marketplace);
      setIsOpenDelMarketplaceWindow(true);
    } else {
      //setMarketplaceToSwitch(marketplace);
      //setIsOpenAddMarketplaceWindow(true);
      if (marketplace === "Yandex") setYmSelected(true)
      if (marketplace === "Wildberries") setWbSelected(true)
      if (marketplace === "Aliexpress") setAliSelected(true)
      onStatusChange({
        disabled: !getIsValid(),
        alert: (<Alert
          type={AlertType.SUCCESS}
          title={t("fbpPage.matchings.addMarketplaceAlert", {
            marketplace: t(`marketplace.${marketplace}`),
          })}
        />),
      });
    }
  }

  const handleSelection = (selected: TItemSearch[]) => {
    setItems([
      ...items,
      ...selected
        .filter((item) => !items.find((v) => (
          (v.id === item.id) &&
          (v.marketplace === item.marketplace)
        )))
        .map((v) => ({ ...v, matchingMemberId: "" })),
    ]);
  };

  const getIsValid = () => {
    return Boolean(name && items.length > 1);
  };

  const createMatchingMutation = useMutation({
    mutationFn: createFBPMatchingRequest,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  const updateMatchingMutation = useMutation({
    mutationFn: updateMatching,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  useEffect(() => {
    if (matchingData) {
      setName(matchingData.name ?? "");
      setItems([
        ...(matchingData.ymProducts ?? []),
        ...(matchingData.wbProducts ?? []),
        ...(matchingData.aliProducts ?? []),
      ]);
    }
    setYmSelected(Boolean(matchingData?.ymProducts?.length))
    setWbSelected(Boolean(matchingData?.wbProducts?.length))
    setAliSelected(Boolean(matchingData?.aliProducts?.length))
  }, [matchingData]);

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(
        createMatchingMutation.isLoading ||
          updateMatchingMutation.isLoading,
      ),
    });
  }, [
    createMatchingMutation.isLoading,
    updateMatchingMutation.isLoading,
    items,
    currentLanguage,
    name,
  ]);

  const onSubmit = () => {
    if (matchingData) {
      updateMatchingMutation.mutate({
        id: matchingData.id,
        name,
        ymProductIds: items.filter(v => isYmRequestItem(v)).map((v) => v.id), // matchingMemberId
        wbProductIds: items.filter(v => isWbRequestItem(v)).map((v) => v.id),
        aliProductIds: items.filter(v => isAliRequestItem(v)).map((v) => v.id),
      });
    } else {
      createMatchingMutation.mutate({
        sellerWarehouseId,
        name,
        ymProductIds: items.filter(v => isYmRequestItem(v)).map((v) => v.id),
        wbProductIds: items.filter(v => isWbRequestItem(v)).map((v) => v.id),
        aliProductIds: items.filter(v => isAliRequestItem(v)).map((v) => v.id),
      });
    }
  };

  useImperativeHandle(formRef, () => ({
    submit: () => setOpenConfirmSaveWindow(true),
  }));

  const removeItemLocally = (id: string) => {
    if (id === "all") {
      // удаляем все
      setItems([]);
    } else {
      setItems(items.filter((item) => id !== item.id));
    }
  };

  const handleRemoveItem = useCallback(
    (id: string, matchingMemberId: string, marketplace?: string) => {
      if (
        ([
          ...(matchingData?.wbProducts ?? []),
          ...(matchingData?.ymProducts ?? []),
          ...(matchingData?.aliProducts ?? []),
        ]).find(
          (v) => (id === "all" || ((v.id === id) && (v.marketplace === marketplace))),
        )
      ) {
        setCurrentDelId(matchingMemberId);
        if (matchingData?.isDeletionAvailable) {
          setIsOpenDelItemWindow(true);
          onStatusChange({ disabled: !getIsValid(), alert: null });
        } else {
          onStatusChange({
            disabled: !getIsValid(),
            alert: (<Alert
              type={AlertType.ERROR}
              title={t("fbpPage.matchings.activeActionsAlert")}
            />),
          });
        }
      } else {
        removeItemLocally(id);
      }
    },
    [items, matchingData],
  );

  const itemsRemoveAvailable = items.length > 2;

  const columns: TableColumn<TMatchingItem>[] = useMemo(
    () => [
      {
        header: t("fbpPage.matchings.columnFoto"),
        renderCell: (row) => <Photo src={row.data.primaryImage} />,
        width: "76px",
        fixWidth: true,
      },
      {
        header: t("fbpPage.matchings.columnMarketplace"),
        renderCell: (row) => (
          <Marketplace name={row.data.marketplace} size="small" />
        ),
        alignment: "center",
        width: "40px",
        fixWidth: true,
      },
      {
        header: t("fbpPage.matchings.columnName"),
        renderCell: (row) => (
          <TableCellLink
            title={row.data.name}
            description={getSku(row.data)}
            thirdLine={
              row.data.sellerName ? (
                <span className={styles.storeLine}>
                  <StoreIcon /> {row.data.sellerName}
                </span>
              ) : undefined
            }
            to={row.data.url}
            external
            maxWidth="540px"
          />
        ),
        alignment: "left",
        width: "600px",
      },
      {
        header: t("fbpPage.matchings.columnSize"),
        valueGetter: (row) => ((isWbRequestItem(row) && row.wbTechSize) || ""),
        width: "160px",
        fixWidth: true,
        alignment: "center",
      },
      {
        header: (
          <Tooltip
            action={
              <IconButton
                icon={DelIcon}
                onClick={() => handleRemoveItem("all", "all")}
                size={IconButtonSize.L}
                type={IconButtonType.ICON}
                disabled={!itemsRemoveAvailable}
              />
            }
            position="top"
            text={
              itemsRemoveAvailable
                ? t("fbpPage.matchings.removeItemTooltip")
                : t("fbpPage.matchings.removeItemDisabledTooltip")
            }
            whiteSpace="pre-wrap"
          />
        ),
        renderCell: (row) =>
          itemsRemoveAvailable ? (
            <IconButton
              icon={DelIcon}
              onClick={() =>
                handleRemoveItem(
                  row.data.id,
                  row.data.matchingMemberId,
                  row.data.marketplace,
                )
              }
              size={IconButtonSize.L}
              type={IconButtonType.ICON}
            />
          ) : null,
        width: "60px",
        fixWidth: true,
      },
    ],
    [currentLanguage, handleRemoveItem, itemsRemoveAvailable],
  );

  const marketplacesAvailable: string[] = [];
  if (hasWbStores) marketplacesAvailable.push("Wildberries");
  if (hasYmStores) marketplacesAvailable.push("Yandex");
  if (hasAliStores) marketplacesAvailable.push("Aliexpress");

  const marketplacesEnabled: string[] = [];
  if (isWbSelected) marketplacesEnabled.push("Wildberries");
  if (isYmSelected) marketplacesEnabled.push("Yandex");
  if (isAliSelected) marketplacesEnabled.push("Aliexpress");

  const views = [
    {
      title: t('fbpPage.matchings.marketplaceSectionTitle'),
      subtitle: t('fbpPage.matchings.marketplaceSectionDescription'),
      id: "marketplaces",
      content: (
        <div className={styles.section}>
          <TextInput
            className={styles.nameInput}
            value={name}
            placeholder={t("fbpPage.matchings.namePlaceholder")}
            onChange={(v) => {
              setName(v);
              setNamePristine(false);
            }}
            onBlur={() => setNamePristine(false)}
            maxLength={50}
            required
            hasError={!name && !isNamePristine}
            title={t("fbpPage.matchings.name")}
          />

          <div className={styles.marketplaces}>
            {hasWbStores ? (<Checkbox
              className={styles.marketplaceCheckbox}
              checked={isWbSelected}
              onChange={(v) => handleMarketplaceSwitch("Wildberries", v)}
              title={(
                <div className={styles.marketplaceLabel}>
                  <Marketplace name="Wildberries" size="large" />
                  <Title2>{t(`marketplace.Wildberries`)}</Title2>
                </div>
              )}
            />) : null}

            {hasYmStores ? (<Checkbox
              className={styles.marketplaceCheckbox}
              checked={isYmSelected}
              onChange={(v) => handleMarketplaceSwitch("Yandex", v)}
              title={(
                <div className={styles.marketplaceLabel}>
                  <Marketplace name="Yandex" size="large" />
                  <Title2>{t(`marketplace.Yandex`)}</Title2>
                </div>
              )}
            />) : null}

            {hasAliStores ? (<Checkbox
              className={styles.marketplaceCheckbox}
              checked={isAliSelected}
              onChange={(v) => handleMarketplaceSwitch("Aliexpress", v)}
              title={(
                <div className={styles.marketplaceLabel}>
                  <Marketplace name="Aliexpress" size="large" />
                  <Title2>{t(`marketplace.Aliexpress`)}</Title2>
                </div>
              )}
            />) : null}
          </div>
        </div>
      ),
    },
    {
      title: t('fbpPage.matchings.itemsSectionTitle'),
      id: "items",
      content: (
        <div className={styles.section}>
          <Title3 color="grey-700">
            {t("fbpPage.matchings.selectItems")}
          </Title3>

          <SearchInput<TItemSearch>
            className={styles.searchInput}
            getItems={(search?: string) =>
              searchItems({
                sellerWarehouseId: sellerWarehouseId,
                search: search ?? "",
                marketplaceTypes: marketplacesEnabled,
              })
            }
            placeholder={t("fbpPage.matchings.searchPlaceholder")}
            requestKey={`searchItems-${sellerWarehouseId}-Ali`}
            keyExtractor={(item: TItemSearch) => item.id}
            MenuComponent={SearchMenuComponentMP}
            minLength={0}
            onItemSelect={handleSelection}
          />
          {items.length ? (
            <Table<TMatchingItem>
              key={JSON.stringify(items)} // todo
              columns={columns}
              data={items ?? []}
            />
          ) : null}
        </div>
      ),
      disabled: !(isWbSelected || isYmSelected || isAliSelected),
    }
  ]

  return (
    <>
      {(marketplacesAvailable.length === 1) ? (
        <div className={styles.form}>
          <Alert
            type={AlertType.NEUTRAL}
            title={t("fbpPage.matchings.modalAlert")}
          />

          <TextInput
            className={styles.nameInput}
            value={name}
            placeholder={t("fbpPage.matchings.namePlaceholder")}
            onChange={(v) => {
              setName(v);
              setNamePristine(false);
            }}
            onBlur={() => setNamePristine(false)}
            maxLength={50}
            required
            hasError={!name && !isNamePristine}
            title={t("fbpPage.matchings.name")}
          />

          <div className={styles.sections}>
            <div className={styles.section}>
              {(marketplacesAvailable[0] === "Wildberries") ? (
                <>
                  <div className={styles.marketplaceRow}>
                    <Marketplace name="Wildberries" size="small" />
                    <Title2>{t(`marketplace.Wildberries`)}</Title2>
                  </div>

                  <Title3 color="grey-700">
                    {t("fbpPage.matchings.wbInputDescription")}
                  </Title3>

                  <SearchInput<TItemSearch>
                    className={styles.searchInput}
                    getItems={(search?: string) =>
                      searchItems({
                        sellerWarehouseId: sellerWarehouseId,
                        search: search ?? "",
                        marketplaceTypes: ["Wildberries"],
                      })
                    }
                    placeholder={t("fbpPage.matchings.searchPlaceholder")}
                    requestKey={`searchItems-${sellerWarehouseId}`}
                    keyExtractor={(item: TItemSearch) => item.id}
                    MenuComponent={SearchMenuComponentMPShort}
                    minLength={0}
                    onItemSelect={handleSelection}
                  />
                  {items.length ? (
                    <Table<TMatchingItem>
                      key={JSON.stringify(items)} // todo
                      columns={columns}
                      data={items ?? []}
                    />
                  ) : null}
                </>
              ) : null}
              {(marketplacesAvailable[0] === "Yandex") ? (
                <>
                  <div className={styles.marketplaceRow}>
                    <Marketplace name="Yandex" size="small" />
                    <Title2>{t(`marketplace.Yandex`)}</Title2>
                  </div>

                  <Title3 color="grey-700">
                    {t("fbpPage.matchings.ymInputDescription")}
                  </Title3>

                  <SearchInput<TItemSearch>
                    className={styles.searchInput}
                    getItems={(search?: string) =>
                      searchItems({
                        sellerWarehouseId: sellerWarehouseId,
                        search: search ?? "",
                        marketplaceTypes: ["Yandex"],
                      })
                    }
                    placeholder={t("fbpPage.matchings.searchPlaceholder")}
                    requestKey={`searchItems-${sellerWarehouseId}`}
                    keyExtractor={(item: TItemSearch) => item.id}
                    MenuComponent={SearchMenuComponentMPShort}
                    minLength={0}
                    onItemSelect={handleSelection}
                  />
                  {items.length ? (
                    <Table<TMatchingItem>
                      key={JSON.stringify(items)} // todo
                      columns={columns}
                      data={items ?? []}
                    />
                  ) : null}
                </>
              ) : null}
              {(marketplacesAvailable[0] === "Aliexpress") ? (
                <>
                  <div className={styles.marketplaceRow}>
                    <Marketplace name="Aliexpress" size="small" />
                    <Title2>{t(`marketplace.Aliexpress`)}</Title2>
                  </div>

                  <Title3 color="grey-700">
                    {t("fbpPage.matchings.AliInputDescription")}
                  </Title3>

                  <SearchInput<TItemSearch>
                    className={styles.searchInput}
                    getItems={(search?: string) =>
                      searchItems({
                        sellerWarehouseId: sellerWarehouseId,
                        search: search ?? "",
                        marketplaceTypes: ["Aliexpress"],
                      })
                    }
                    placeholder={t("fbpPage.matchings.searchPlaceholder")}
                    requestKey={`searchItems-${sellerWarehouseId}`}
                    keyExtractor={(item: TItemSearch) => item.id}
                    MenuComponent={SearchMenuComponentMPShort}
                    minLength={0}
                    onItemSelect={handleSelection}
                  />
                  {items.length ? (
                    <Table<TMatchingItem>
                      key={JSON.stringify(items)} // todo
                      columns={columns}
                      data={items ?? []}
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <Accordeon
          defaultOpenId={accordeonPage}
          items={views}
          className={styles.accordeon}
          summaryClassName={styles.accordeonSummary}
          contentClassName={styles.accordeonContent}
          onChange={setAccordeonPage}
        />
      )}

      {isOpenConfirmSaveWindow && (
        <ConfirmModal
          key={`${currentDelId}`}
          title={t("fbpPage.matchings.createMatchingConfirmTitle")}
          description={t("fbpPage.matchings.createMatchingConfirmDescription")}
          noBtnTitle={t("fbpPage.matchings.createMatchingConfirCancelBtn")}
          yesBtnTitle={t(
            `fbpPage.matchings.${matchingData?.id ? "updateMatchingConfirmOkBtn" : "createMatchingConfirmOkBtn"}`
          )}
          onClose={() => setOpenConfirmSaveWindow(false)}
          requestFn={onSubmit}
          onSuccess={() => {}}
          icon={<InfoIcon className={styles.infoIcon} />}
          inModal
          yesBtnType={ButtonType.PRIMARY}
        />
      )}

      {isOpenDelItemWindow && (
        <ConfirmModal
          key={currentDelId}
          title={t("fbpPage.matchings.removeItemTitle")}
          description={t("fbpPage.matchings.removeItemDescription")}
          noBtnTitle={t("fbpPage.matchings.removeItemCancelBtn")}
          yesBtnTitle={t("fbpPage.matchings.removeItemOkBtn")}
          onClose={() => setIsOpenDelItemWindow(false)}
          requestFn={() => {
            const newItems =
              currentDelId === "all"
                ? []
                : items.filter((v) => v.matchingMemberId !== currentDelId);
            setItems(newItems);
          }}
          onSuccess={() => {}}
          icon={<DelIcon2 />}
          inModal
        />
      )}

      {isOpenDelMarketplaceWindow && (
        <ConfirmModal
          key={currentDelId}
          title={t("fbpPage.matchings.removeMarketplaceModal.title")}
          description={t("fbpPage.matchings.removeMarketplaceModal.description", {
            marketplace: t(`marketplace.${marketplaceToSwitch}`),
          })}
          noBtnTitle={t("fbpPage.matchings.removeMarketplaceModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.matchings.removeMarketplaceModal.okBtn")}
          onClose={() => setIsOpenDelMarketplaceWindow(false)}
          requestFn={() => {
            // подтвердили отключение маркетплейса - удаляем все товары этого маркеплейса из связки
            const newItems = items.filter((v) => v.marketplace !== marketplaceToSwitch);
            setItems(newItems);
            if (marketplaceToSwitch === "Yandex") setYmSelected(false)
            if (marketplaceToSwitch === "Wildberries") setWbSelected(false)
            if (marketplaceToSwitch === "Aliexpress") setAliSelected(false)
            setTimeout(() => {
              onStatusChange({
                disabled: !getIsValid(),
                alert: (<Alert
                  type={AlertType.SUCCESS}
                  title={t("fbpPage.matchings.delMarketplaceAlert", {
                    marketplace: t(`marketplace.${marketplaceToSwitch}`),
                  })}
                />),
              });
            }, 100)
          }}
          onSuccess={() => {}}
          icon={<InfoIcon />}
          inModal
        />
      )}
    </>
  );
};

export default Form;
